import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class CartGiftPicker extends Component {
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    document.addEventListener(CartUpdateEvent.eventName, this.#onCartUpdate);
    this.#syncGiftState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(CartUpdateEvent.eventName, this.#onCartUpdate);
  }

  #onCartUpdate = async () => {
    await this.#syncGiftState();
  };

  #onClick = async (event) => {
    const button = event.target.closest('.cart-gift-picker__button');
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.disabled || button.getAttribute('aria-pressed') === 'true') return;

    const variantId = button.dataset.giftVariantId;
    const sectionId = this.dataset.sectionId;
    if (!variantId || !sectionId) return;

    const previousVariantId = this.querySelector(
      '.cart-gift-picker__button[aria-pressed="true"]'
    )?.dataset.giftVariantId;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    try {
      this.classList.add('cart-gift-picker--loading');
      this.#setSelectedVariant(variantId);
      await this.#removeCurrentGift(signal);

      const addResponse = await fetch(`${Theme.routes.cart_add_url}`, {
        ...fetchConfig('json', {
          body: JSON.stringify({
            items: [
              {
                id: Number(variantId),
                quantity: 1,
                properties: {
                  _gift: 'true',
                },
              },
            ],
            sections: [sectionId],
            sections_url: window.location.pathname,
          }),
        }),
        signal,
      });

      const data = await addResponse.json();
      if (data.status) {
        throw new Error(data.description || data.message || 'Gift add failed');
      }

      if (data.sections?.[sectionId]) {
        morphSection(sectionId, data.sections[sectionId]);
        document.dispatchEvent(
          new CartUpdateEvent({}, sectionId, {
            itemCount: data.item_count,
            source: 'cart-gift-picker',
            sections: data.sections,
          })
        );
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
        if (previousVariantId) {
          this.#setSelectedVariant(previousVariantId);
        } else {
          this.#resetButtonStates();
        }
      }
    } finally {
      this.classList.remove('cart-gift-picker--loading');
    }
  };

  #setSelectedVariant(variantId) {
    const labelAdd = this.dataset.labelAdd || 'Dodaj';
    const labelAdded = this.dataset.labelAdded || 'Dodano';

    this.querySelectorAll('.cart-gift-picker__slide').forEach((slide) => {
      const button = slide.querySelector('.cart-gift-picker__button');
      const card = slide.querySelector('.cart-gift-picker__card');
      const label = button?.querySelector('.cart-gift-picker__button-text');
      if (!button || !card || !label) return;

      const isSelected = button.dataset.giftVariantId === variantId;
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      button.classList.toggle('cart-gift-picker__button--selected', isSelected);
      button.classList.toggle('button--secondary', !isSelected);
      card.classList.toggle('cart-gift-picker__card--selected', isSelected);
      label.textContent = isSelected ? labelAdded : labelAdd;
    });
  }

  #resetButtonStates() {
    const selectedVariantId =
      this.querySelector('.cart-gift-picker__button[aria-pressed="true"]')?.dataset.giftVariantId ||
      this.dataset.selectedVariantId;

    if (selectedVariantId) {
      this.#setSelectedVariant(selectedVariantId);
      return;
    }

    const labelAdd = this.dataset.labelAdd || 'Dodaj';
    this.querySelectorAll('.cart-gift-picker__slide').forEach((slide) => {
      const button = slide.querySelector('.cart-gift-picker__button');
      const card = slide.querySelector('.cart-gift-picker__card');
      const label = button?.querySelector('.cart-gift-picker__button-text');
      if (!button || !card || !label) return;

      button.setAttribute('aria-pressed', 'false');
      button.classList.remove('cart-gift-picker__button--selected');
      button.classList.add('button--secondary');
      card.classList.remove('cart-gift-picker__card--selected');
      label.textContent = labelAdd;
    });
  }

  async #syncGiftState() {
    const sectionId = this.dataset.sectionId;
    const threshold = Number(this.dataset.threshold || 0);
    const eligibleTotal = Number(this.dataset.eligibleTotal || 0);
    const giftLineKey = this.dataset.giftLineKey;

    if (!sectionId || !giftLineKey) return;
    if (eligibleTotal >= threshold) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      await this.#removeCurrentGift(this.#abortController.signal, sectionId);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    }
  }

  async #removeCurrentGift(signal, sectionId = this.dataset.sectionId) {
    const giftLineKey = this.dataset.giftLineKey;
    if (!giftLineKey || !sectionId) return;

    const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { signal });
    const cart = await cartResponse.json();
    const lineIndex = cart.items.findIndex((item) => item.key === giftLineKey);
    if (lineIndex === -1) return;

    const changeResponse = await fetch(`${Theme.routes.cart_change_url}`, {
      ...fetchConfig('json', {
        body: JSON.stringify({
          line: lineIndex + 1,
          quantity: 0,
          sections: [sectionId],
          sections_url: window.location.pathname,
        }),
      }),
      signal,
    });

    const data = await changeResponse.json();
    if (data.sections?.[sectionId]) {
      morphSection(sectionId, data.sections[sectionId]);
      document.dispatchEvent(
        new CartUpdateEvent({}, sectionId, {
          itemCount: data.item_count,
          source: 'cart-gift-picker',
          sections: data.sections,
        })
      );
    }
  }
}

if (!customElements.get('cart-gift-picker')) {
  customElements.define('cart-gift-picker', CartGiftPicker);
}
